export interface BrandSuggestionParams {
  prefix?: string; // the displayName
  companyNamePrefix?: string;
  status?: string;
  limit?: number;
}

export interface BrandSuggestion {
  brandUid: string;
  brandName: string; // the displayName
  brandCompanyName: string;
}

export interface CspSuggestionParams {
  prefix?: string; // the displayName prefix
  companyNamePrefix?: string;
  limit?: number;
}

export interface CspSuggestion {
  cspUid: string;
  cspName: string; // the displayName
  cspCompanyName: string;
}

export interface MnoProfile {
  mnoId: number;
  email: string;
  displayName: string;
  mfaEnabled: boolean;
  dpaSigned: boolean;
  maxPortalUsers: number;
  market: string;
  rcsVisible: boolean;
}

export interface User {
  id: string;
  status: string;
  created: number;
  statusChanged: number;
  lastLogin?: number;
  lastUpdated: number;
  profile: {
    firstName: string;
    lastName: string;
    email: string;
    mnoNetworkId: number;
    companyName: string;
    fullName: string;
  };
}
export type BrandEntityType =
  | 'GOVERNMENT'
  | 'NON_PROFIT'
  | 'PRIVATE_PROFIT'
  | 'PUBLIC_PROFIT'
  | 'SOLE_PROPRIETOR';

export interface Csp {
  uid: string;
  displayName: string;
  companyName: string;
  ein: string;
  phone: string;
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  email: string;
  stockSymbol?: string;
  stockExchange?: string;
  website: string;
  entityType: string;
  activeCampaignCount?: number;
}
export interface Brand {
  uid: string;
  cspUid: string;
  displayName: string;
  entityType: BrandEntityType;
  companyName: string;
  ein: string;
  einIssuingCountry: string;
  phone: string;
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  email: string;
  status?: string;
  stockSymbol?: string;
  stockExchange?: string;
  brandRelationshipScore: number;
  website?: string;
  submissionCount: number;
  vettedDate: number;
  vertical: string;
  trustLevel: number;
  createDate: number;
  updateDate: number;
  universalEin: string;
  identityStatus: string;
  optionalAttributes: Record<string, unknown>;
  activeCampaignCount?: number;
  altBusinessIdType?: string;
  altBusinessId?: string;
  csp: Csp;
  businessContactEmail: string | null;
  businessContactFirstName: string | null;
  businessContactLastName: string | null;
  businessContactTitle: string | null;
  businessContactEmailVerifiedDate: number | null;
  trademark?: string;
  senderIdForm: string;
  loaStatus: string | null;
  trademarkStatus: string | null;
}

export interface BrandDetail {
  uid: string;
  cspUid: string;
  status: string;
  displayName: string;
  entityType: string;
  companyName: string;
  ein: string;
  einIssuingCountry: string;
  phone: string;
  street: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  email: string;
  stockSymbol?: string;
  stockExchange?: string;
  brandRelationshipScore: number;
  altBusinessIdType?: string;
  altBusinessId?: string;
  website?: string;
  submissionCount: number;
  vettedDate?: number;
  vertical?: string;
  trustLevel: number;
  createDate: number;
  updateDate: number;
  universalEin: string;
  identityStatus: string;
  optionalAttributes: Record<string, unknown>;
  businessContactEmail?: string | null;
  businessContactEmailVerifiedDate?: number | null;
  businessContactFirstName?: string | null;
  businessContactLastName?: string | null;
  businessContactTitle?: string | null;
  activeCampaignCount?: number;
  csp: {
    uid: string;
    displayName: string;
    companyName: string;
    ein: string;
    phone: string;
    street: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
    email: string;
    stockSymbol?: string;
    stockExchange?: string;
    website: string;
    entityType: string;
    market: string;
    activeCampaignCount: number;
  };
  rcsVettingStatus?: string;
  logoStatus?: string;
  bannerStatus?: string;
  trademarkStatus: string | null;
  loaStatus: string | null;
}
export interface CampaignListItem {
  uid: string;
  googleAgentId: string;
  brandUid: string;
  cspUid: string;
  resellerUid: string;
  usecase: string;
  status: string;
  createDate: number;
  mnoStatus: string;
  mnoDcaUid: string;
  mnoDcaName: string;
  mnoSuspensionRole: string;
  mnoSuspensionDate: number;
  mnoSuspensionExplanation: string;
  brandName: string;
  cspName: string;
  billingCategory: string;
  attestationStatus: string;
  countryCode: string;
  channelId: Channel;
  senderIds?: SenderId[];
}

export interface UsecaseType {
  id: string;
  classification: string;
  displayName: string;
  description: string;
  monthlyFee: number;
  quarterlyFee: number;
  annualFee: number;
  validSubUsecase: boolean;
  minSubUsecases: number;
  maxSubUsecases: number;
  maxTn?: number;
}

export type Channel = '10DLC' | 'RCS' | 'SMS';

export type ApprovedDca = {
  uid: string;
  displayName: string;
};

export interface CampaignDetail {
  uid: string;
  googleAgentId: string | null;
  brandUid: string;
  cspUid: string;
  resellerUid: string;
  tpm: number;
  description: string;
  usecase: string;
  subUsecases: string;
  status: string;
  campaignScale: string;
  embeddedLink: boolean;
  embeddedPhone: boolean;
  termsAndConditions: boolean;
  ageGated: boolean;
  directLending: boolean;
  numberPool: boolean;
  subscriberOptin: boolean;
  subscriberOptout: boolean;
  subscriberHelp: boolean;
  sample1: string;
  sample2: string | null;
  sample3: string | null;
  sample4: string | null;
  sample5: string | null;
  messageFlow: string;
  optinKeywords: string | null;
  optoutKeywords: string | null;
  helpKeywords: string | null;
  optinMessage: string | null;
  optoutMessage: string | null;
  helpMessage: string | null;
  privacyPolicyLink: string | null;
  termsAndConditionsLink: string | null;
  embeddedLinkSample: string | null;
  createDate: number;
  updateDate: number;
  operationStatus: string;
  rulesEngineResultsJson: string | null;
  brandName: string;
  brand: {
    uid: string;
    cspUid: string;
    status: string;
    displayName: string;
    entityType: string;
    companyName: string;
    ein: string;
    einIssuingCountry: string;
    phone: string;
    street: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
    email: string;
    stockSymbol: string | null;
    stockExchange: string | null;
    brandRelationshipScore: number;
    altBusinessIdType: string | null;
    altBusinessId: string | null;
    website: string | null;
    submissionCount: number;
    vettedDate: number;
    vertical: string | null;
    trustLevel: number;
    createDate: number;
    updateDate: number;
    universalEin: string;
    identityStatus: string;
    optionalAttributes: Record<string, unknown>;
    businessContactEmail: string | null;
    businessContactEmailVerifiedDate: number | null;
    businessContactFirstName: string | null;
    businessContactLastName: string | null;
    businessContactTitle: string | null;
    activeCampaignCount: number | null;
    rcsVettingStatus: string | null;
    logoStatus: string | null;
    bannerStatus: string | null;
    csp: {
      uid: string;
      displayName: string;
      companyName: string;
      ein: string;
      phone: string;
      street: string;
      city: string;
      state: string;
      postalCode: string;
      country: string;
      email: string;
      stockSymbol: string | null;
      stockExchange: string | null;
      website: string;
      entityType: string;
      market: string;
      activeCampaignCount: number | null;
    };
  };
  reseller: {
    uid: string;
    cspUid: string | null;
    companyName: string;
    phone: string | null;
    email: string | null;
  };
  phoneNumbers: Array<string> | null;
  dcaName: string;
  countryCode: string;
  channelId: string;
  displayName: string | null;
  hostingRegion: string | null;
  billingCategory: string | null;
  shortDescription: string | null;
  color: string | null;
  phones: Array<string> | null;
  phoneLabels: Array<string> | null;
  emails: Array<string> | null;
  emailLabels: Array<string> | null;
  websites: Array<string> | null;
  websiteLabels: Array<string> | null;
  technicalContactName: string | null;
  technicalContactEmail: string | null;
  technicalContactJobTitle: string | null;
  campaignLogo: string | null;
  campaignBanner: string | null;
  screenshotUrl: string | null;
  videoUrl: string | null;
  attestationContactName: string | null;
  attestationJobTitle: string | null;
  attestationPhone: string | null;
  attestationEmail: string | null;
  attestationStatus: string | null;
  actionTriggerMessages: string | null;
  messageTypes: string | null;
  optinFlow: string | null;
  optoutFlow: string | null;
  fallbackCampaignUid: string | null;
  routingMode: string | null;
  senderIdType: string | null;
  offNetOutboundTerminationSupported: boolean;
  intlInboundTerminationSupported: boolean;
  intlOutboundTerminationSupported: boolean;
  onNetInboundTerminationSupported: boolean;
  offNetInboundTerminationSupported: boolean;
  targetMNOs: number[] | null;
}

export interface BrandAsset {
  name: string;
  type: string;
  attachmentUuid: string;
  verificationStatus: string;
  reasons: string;
  createDate: number;
  verifiedDate?: number;
  validUntil?: number;
  expirationDate?: number;
}

export interface CampaignSharingChain {
  campaignUid: string;
  cspUid: string;
  nodes: [
    {
      role: string;
      uid: string;
      acceptOrElectDate?: number;
      myDca?: boolean;
      proxyConnectorUid?: string;
      proxyConnectorName?: string;
      sharingStatus?: string;
      declineDate?: number;
      rejectionCategoryList?: Array<string>;
      explanation?: string;
      displayName: string;
    }
  ];
}

export interface CampaignVerification {
  campaignUid: string;
  type: string;
  value: string; // google agent ID
  evpUid: string;
  vettingId: string | null;
  vettingToken: string | null;
  attachmentUuidList: string | null;
  verificationStatus: string;
  verificationDate: number | null;
  validUntil: number | null;
  expirationDate: number | null;
  reasons: Array<string>;
  fullReport: Record<string, any>;
  mnoIds: Array<number>;
  createDate: number;
  updateDate: number;
}

export interface EventHistory {
  id: number;
  eventType: string;
  categoryType: string;
  campaignUid: string;
  cspUid: string;
  dcaUid: string;
  mnoId: number;
  evpUid: string;
  brandUid: string;
  description: string;
  createDate: number;
  updateDate: number;
  eventTypeDescription: string;
  source: string;
  dcaName: string;
  brandName: string;
  authenticatedUser: string | null;
}

export enum BrandAssetStatus {
  Pending = 'PENDING',
  Verified = 'VERIFIED',
  Unverified = 'UNVERIFIED',
  Expired = 'EXPIRED',
}

export interface BrandVettingRecord {
  pUid: string;
  brandUid: string;
  vettingClass: string;
  vettingId: string;
  vettingStatus: string;
  vettingToken?: string;
  additionalRequestPayload?: string;
  fullReport?: string;
  reasons?: string;
  score?: number;
  tcrBillable: boolean;
  tcrBillAmount?: number;
  createDate: number | Date;
  updateDate: number | Date;
  vettedDate?: number | Date;
  expirationDate?: number | Date;
  evpUid: string;
  validUntil: number;
}

export interface BrandTrademark {
  id: string;
  uid: string;
  cspUid: string;
  brandUid: string;
  registrationId: string;
  trademarkText: string;
  owner: string;
  patentOffice: string;
  validUntil: number | null;
  outcome: string | null;
  updateDate: number;
  createDate: number;
  description: string | null;
}

export interface BrandSenderId {
  id: number;
  uid: string;
  cspUid: string;
  brandUid: string;
  senderId: string;
  associatedDcaList: string;
  protectedSenderIdList: string;
  updateDate: number;
  createDate: number;
  trademarkUid: string | null;
}

export interface BrandLOA {
  docUid: string;
  envelopeUuid: string;
  email: string;
  templateType: string;
  entityType: string;
  entityUid: string;
  content: string;
  status: string;
  createDate: number;
  updateDate: number;
  signDate: number;
}

export interface AuthenticatedUserSuggestionParams {
  prefix?: string;
}

export interface AuthenticatedUserSuggestion {
  type: string;
  scope: string;
  name: string;
  identifier: string;
}

export enum BrandStatus {
  Active = 'ACTIVE',
  Deleted = 'DELETED',
}

export interface VerticalType {
  id: string;
  displayName: string;
  description: string;
}

export interface CampaignStatusParams {
  status: string;
  explanation: string;
  suspensionCategory?: string;
}

export interface ComplaintParams {
  details: string;
}

export interface SenderId {
  id: number;
  uid: string;
  networkId: number;
  senderIdValue: string;
  senderIdType: string;
  market: string;
  sharingModel: string;
  routingMode: string;
  status: string;
  createDate: number;
  updateDate: number;
  campaignUid: string | null;
  dcaUid: string | null;
  dcaName: string | null;
  internationalInbound: boolean;
  internationalOutbound: boolean;
  onNetInbound: boolean;
  offNetInbound: boolean;
  offNetOutbound: boolean;
}

export interface Mno {
  networkId: number;
  displayName: string;
  osrBitmaskIndex: number;
}

export interface CampaignLoa {
  docUid: string;
  type: string;
  entityUid: string;
  dcaUid: string;
  entityType: string;
  status: string;
  createDate: number;
  updateDate: number;
  signDate: number;
}

export interface CampaignLoaDocParams {
  format?: string;
}

export interface Attachment {
  uuid: string;
  fileName: string;
  mimeType: string;
  file?: File;
}
